// steps/UhrzeitStep.tsx

import React, { Fragment, useEffect, useState } from "react";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import { useQuery } from "@tanstack/react-query";

import { useAllowScroll } from "../AllowScrollContext";
import { MaterialSummary, SummaryContainer } from "./Summaries";
import { MaterialOrder } from "@/components/planning/Project";
import ApiService from "@/api/ApiService";
import SpecialTimePicker from "../../SpecialTimePicker";

type RouteInfo = {
  loadingTime: number | null;
  routeTime: number | null;
  inversed: boolean;
  plantAvailability: {
    isClosingDay: boolean;
    closingDayComment: string | null;
    earliestLoadingTime: string | null;
  };
};

export default function UhrzeitStep({
  order,
  jobId,
  onBack,
  jobPosition,
  expertMode,
  onSelect,
}: {
  order: MaterialOrder;
  jobId: number;
  jobPosition: any;
  expertMode: boolean;
  onBack: () => void;
  onSelect: (patch: Partial<MaterialOrder>) => void;
}) {
  const { setAllowScroll } = useAllowScroll();

  useEffect(() => {
    setAllowScroll(false);
  }, [setAllowScroll]);

  const [localFirstUnload, setLocalFirstUnload] = useState(order.firstUnload ?? null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!expertMode) return;
    onSelect({ firstUnload: localFirstUnload });
  }, [expertMode, localFirstUnload]);

  const isBaustellendienst = order.amount === -1 && !order.article;
  const isBauhof = !!order.article && order.article.bauhof > 0;

  const { data: routeInfo, isLoading } = useQuery<null | RouteInfo>({
    queryKey: [
      "routeInfo",
      {
        article: order.article?.id || 0,
        position: order.position?.id || 0,
        easyTarget: order.easyTarget,
        job: jobId,
        jobPosition,
      },
    ],
    queryFn: ApiService.routeInfo(),
    enabled: true,
  });

  const handleContinue = () => {
    setSaving(true);
    onSelect({ firstUnload: localFirstUnload });
    setSaving(false);
  };

  return (
    <Box>
      {!expertMode ? (
        <Fragment>
          <SummaryContainer>
            <MaterialSummary includeAmount order={order} isBauhof={isBauhof} isBaustellendienst={isBaustellendienst} />
          </SummaryContainer>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography sx={{ fontSize: 18 }}>Uhrzeit festlegen</Typography>
          </Box>
        </Fragment>
      ) : null}

      {isLoading ? (
        <Stack sx={{ height: 100 }} alignItems="center" justifyContent="center">
          <CircularProgress size={24} />
        </Stack>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography variant="button" sx={{ userSelect: "none" }}>
            Erste Lieferung
          </Typography>

          <SpecialTimePicker
            notApplicable={(order.article?.bauhof ?? 0) > 0}
            value={localFirstUnload}
            onChange={setLocalFirstUnload}
            routeInfo={routeInfo}
          />

          {expertMode ? null : saving ? (
            <Box sx={{ mt: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
              <Button variant="contained" onClick={onBack} startIcon={<ArrowBackIcon />} sx={{ minWidth: 48 }}>
                Zurück
              </Button>

              <Button variant="contained" color="success" startIcon={<CheckIcon />} fullWidth onClick={handleContinue} sx={{ flex: 1 }}>
                Weiter
              </Button>
            </Stack>
          )}
        </Box>
      )}
    </Box>
  );
}
